import type { Clock } from './types';

/**
 * A clock the replay harness and tests drive by hand. Timers fire in order as time advances;
 * after each callback the microtask queue is flushed so async work (the speech queue's drain)
 * settles before the next timer or fix.
 */
export class VirtualClock implements Clock {
  private t: number;
  private timers = new Map<number, { at: number; fn: () => void }>();
  private seq = 0;

  constructor(start = 0) {
    this.t = start;
  }

  now(): number {
    return this.t;
  }

  setTimeout(fn: () => void, ms: number): unknown {
    const id = ++this.seq;
    this.timers.set(id, { at: this.t + Math.max(0, ms), fn });
    return id;
  }

  clearTimeout(handle: unknown): void {
    this.timers.delete(handle as number);
  }

  get pendingTimers(): number {
    return this.timers.size;
  }

  private nextDue(to: number): { id: number; at: number; fn: () => void } | null {
    let best: { id: number; at: number; fn: () => void } | null = null;
    for (const [id, tm] of this.timers) {
      if (tm.at <= to && (best == null || tm.at < best.at)) best = { id, ...tm };
    }
    return best;
  }

  async advance(to: number): Promise<void> {
    for (;;) {
      // Let any in-flight async work (e.g. a drain suspended at an await) schedule its timers first.
      await flushMicrotasks();
      const next = this.nextDue(to);
      if (!next) break;
      this.timers.delete(next.id);
      this.t = Math.max(this.t, next.at);
      next.fn();
    }
    this.t = Math.max(this.t, to);
    await flushMicrotasks();
    // Zero-delay timers scheduled during that flush land exactly at `to`; fire them too.
    if (this.nextDue(to)) await this.advance(to);
  }

  async tick(ms: number): Promise<void> {
    await this.advance(this.t + ms);
  }
}

export async function flushMicrotasks(rounds = 8): Promise<void> {
  for (let i = 0; i < rounds; i++) await Promise.resolve();
}
